/**
 * Admin Routes Component
 * Defines all routes for the Admin Portal (Control Plane)
 * Completely isolated from CRM routes
 */
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { AdminAuthProvider, useAdminAuth } from './auth/AdminAuthContext';
import { permissionForPath } from './navigation';
import AdminLoginPage from './auth/AdminLoginPage';
import AdminLayout from './layout/AdminLayout';
import AdminDashboard from './AdminDashboard';
import { TenantsPage, TenantDetailPage, CreateTenantPage } from './tenants';
import { UsersPage, UserDetailPage } from './users';
import { SubscriptionsPage } from './subscriptions';
import { CommercialPage } from './commercial';
import { ModulesPage } from './modules';
import { LicenseCatalogPage } from './licenses';
import { ReleasesPage } from './releases';
import { AuditLogsPage } from './audit';
import { OperatorsPage } from './operators';
import { TenantUsagePage, LimitsTemplatesPage } from './quotas';
import { IntegrationsPage } from './integrations';
import { ConnectionProfilesPage } from './connections';
import { PlatformDriftPage } from './drift';
import { PlaceholderPage } from './components';
import { Loader2, ShieldOff } from 'lucide-react';

/**
 * Protected Route for Admin Portal
 * Only allows authenticated platform administrators
 */
const AdminProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAdminAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
          <p className="text-slate-600">Loading Admin Portal...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Carry the return path: a session that ends on /tenants/t-1 must bring
    // the operator back to /tenants/t-1 after they sign in, not to the
    // dashboard. The provider also remembers it in sessionStorage for the
    // case where the guard is re-mounted from scratch.
    const from = `${location.pathname}${location.search || ''}${location.hash || ''}`;
    return <Navigate to="/login" replace state={{ from }} />;
  }

  return children;
};

/**
 * Permission gate for a screen.
 *
 * AdminProtectedRoute only ever asked "are you signed in?", so any operator who
 * could log in could render any screen by typing or bookmarking its URL — a
 * Billing operator, who cannot create tenants, got the whole Create Tenant form
 * and only found out on submit. The sidebar hid the link; nothing enforced it.
 *
 * This is a correctness and clarity fix, not a security boundary: every route
 * behind these screens is enforced server-side by require_operator_permission,
 * so the data was never reachable. What was wrong was rendering a screen the
 * operator can only fail at.
 *
 * Fails closed twice over: an unknown path and unloaded capabilities both deny,
 * because can() is false until the backend has answered.
 */
const RequirePermission = ({ children }) => {
  const { can, capabilitiesLoaded, capabilityError } = useAdminAuth();
  const location = useLocation();
  const screen = permissionForPath(location.pathname);

  if (!capabilitiesLoaded) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (screen && can(screen.permission)) {
    return children;
  }

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center px-6 text-center">
      <ShieldOff className="mb-3 h-8 w-8 text-slate-400" />
      <h2 className="text-lg font-semibold text-slate-900">
        You do not have access to this page
      </h2>
      <p className="mt-1 max-w-md text-sm text-slate-500">
        {capabilityError
          ? 'Your permissions could not be loaded, so nothing is shown. Reload to retry.'
          : screen
            ? `This page requires the ${screen.permission} permission. Ask an Owner if you need it.`
            : 'This page is not part of the Admin Portal.'}
      </p>
    </div>
  );
};

/**
 * Admin Routes Definition
 */
const AdminRoutesContent = () => {
  return (
    <Routes>
      {/* Public admin routes */}
      <Route path="/login" element={<AdminLoginPage />} />
      
      {/* Protected admin routes */}
      <Route
        path="/"
        element={
          <AdminProtectedRoute>
            <AdminLayout />
          </AdminProtectedRoute>
        }
      >
        {/* Dashboard */}
        <Route index element={<RequirePermission><AdminDashboard /></RequirePermission>} />
        
        {/* Tenant Management */}
        <Route path="tenants" element={<RequirePermission><TenantsPage /></RequirePermission>} />
        <Route path="tenants/create" element={<RequirePermission><CreateTenantPage /></RequirePermission>} />
        <Route path="tenants/:tenantId" element={<RequirePermission><TenantDetailPage /></RequirePermission>} />
        <Route path="tenants/:tenantId/edit" element={
          <RequirePermission>
            <PlaceholderPage 
              title="Edit Tenant" 
              description="Edit tenant details"
            />
          </RequirePermission>
        } />
        <Route path="tenants/:tenantId/usage" element={<RequirePermission><TenantUsagePage /></RequirePermission>} />
        
        {/* Users (Platform Monitoring) */}
        <Route path="users" element={<RequirePermission><UsersPage /></RequirePermission>} />
        <Route path="users/:userId" element={<RequirePermission><UserDetailPage /></RequirePermission>} />
        
        {/* Platform Configuration */}
        <Route path="license-catalog" element={<RequirePermission><LicenseCatalogPage /></RequirePermission>} />
        <Route path="releases" element={<RequirePermission><ReleasesPage /></RequirePermission>} />
        <Route path="subscriptions" element={<RequirePermission><SubscriptionsPage /></RequirePermission>} />
        <Route path="commercial" element={<RequirePermission><CommercialPage /></RequirePermission>} />
        <Route path="modules" element={<RequirePermission><ModulesPage /></RequirePermission>} />
        <Route path="integrations" element={<RequirePermission><IntegrationsPage /></RequirePermission>} />
        <Route path="quotas" element={<RequirePermission><LimitsTemplatesPage /></RequirePermission>} />

        {/* Connections */}
        <Route path="connections/profiles" element={<RequirePermission><ConnectionProfilesPage /></RequirePermission>} />
        
        {/* Operations */}
        <Route path="audit-logs" element={<RequirePermission><AuditLogsPage /></RequirePermission>} />
        <Route path="platform-drift" element={<RequirePermission><PlatformDriftPage /></RequirePermission>} />

        {/* Access */}
        <Route path="operators" element={<RequirePermission><OperatorsPage /></RequirePermission>} />
      </Route>

      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

/**
 * Admin Routes with Auth Provider
 */
const AdminRoutes = () => {
  return (
    <AdminAuthProvider>
      <AdminRoutesContent />
    </AdminAuthProvider>
  );
};

export default AdminRoutes;
